import { socketService, GameRoom } from '../services/SocketService';
import { Board } from '../game/board';
import { GameEngine } from '../game/gameEngine';

export class GameplayScreen {
  private container: HTMLElement;
  private currentGame: GameRoom;
  private board: Board;
  private engine: GameEngine;
  private isMyTurn: boolean = false;
  private chessClockTimer: NodeJS.Timeout | null = null;
  private player1TimeRemaining: number = 300; // 5 minutes in seconds
  private player2TimeRemaining: number = 300; // 5 minutes in seconds
  private readonly CHESS_CLOCK_TOTAL_TIME = 300;
  private lastOpponentId: string | null = null;
  // Local, non-persistent match history (keeps last N matches for this client session)
  private localMatchHistory: Array<{
    opponentName: string;
    result: 'win' | 'loss' | 'draw';
    eloChange: number;
    timestamp: number;
  }> = [];
  // Track the most recent move to highlight the cell
  private lastMove: { row: number; col: number } | null = null;
  private winningLinePositions: Array<{ row: number; col: number }> | null =
    null;
  private rematchRequested: boolean = false;
  private gameInProgress: boolean = true;
  private onExitToLobby: () => void;
  private _chessClockHandler:
    | ((data: {
        gameId: string;
        player1Time: number;
        player2Time: number;
        currentPlayer: 'PLAYER_1' | 'PLAYER_2';
      }) => void)
    | null = null;

  constructor(
    container: HTMLElement,
    game: GameRoom,
    callbacks: {
      onExitToLobby: () => void;
    }
  ) {
    this.container = container;
    this.currentGame = game;
    this.onExitToLobby = callbacks.onExitToLobby;

    // Initialize board and engine
    this.board = new Board(game.boardSize);

    const myPlayerId = socketService.socketId;
    const myRole = game.players.find((p) => p.socketId === myPlayerId)?.role;
    this.isMyTurn = myRole === 'PLAYER_1';

    const player1Name = game.players[0]?.name || 'Player 1';
    const player2Name = game.players[1]?.name || 'Player 2';

    this.engine = new GameEngine(
      player1Name,
      player2Name,
      game.boardSize,
      game.requireOpenEnd
    );

    // Store opponent for rematch
    const opponent = this.currentGame.players.find(
      (p) => p.socketId !== socketService.socketId
    );
    this.lastOpponentId = opponent?.socketId || null;
  }

  public render(): void {
    this.container.innerHTML = `
      <div class="gameplay-container">
        <div class="gameplay-left-panel">
          <div class="game-controls" id="game-controls">
            <button id="resign-btn-gameplay" class="danger-btn">🏳️ Resign Game</button>
            <button id="exit-to-lobby-btn-gameplay" class="secondary-btn">↩️ Exit to Lobby</button>
            <button id="rematch-btn-gameplay" class="primary-btn hidden">🔄 Request Rematch</button>
            <div id="rematch-status-gameplay" class="rematch-status"></div>
          </div>
 
          <div class="local-match-history" id="local-match-history">
            <h4>Match history (local)</h4>
            <div id="local-match-list" class="local-match-list">
              <p class="empty">No recent matches</p>
            </div>
          </div>
        </div>
       
        <div class="gameplay-main">
          <div id="chess-clock" class="chess-clock">
            <span class="player-symbol">🞩</span>
            <span id="player1-name" class="player-name">${
              this.currentGame.players[0]?.name || 'Player 1'
            }</span>
            <span id="player1-time" class="time-display">04:00</span>
            <span class="clock-divider">|</span>
            <span id="player2-time" class="time-display">04:00</span>
            <span id="player2-name" class="player-name">${
              this.currentGame.players[1]?.name || 'Player 2'
            }</span>
            <span class="player-symbol">🞅</span>
          </div>
         
          <div id="game-board-container" class="game-board-container">
            <!-- Board will be rendered here -->
          </div>
        </div>
      </div>
    `;

    this.attachEventListeners();
    this.renderBoard();
    this.updateGameStatus();
    // Start listening for server clock updates instead of running a local timer
    // Register a named handler so we can remove it later
    this._chessClockHandler = (data) => {
      if (data.gameId !== this.currentGame?.id) return;
      this.player1TimeRemaining = data.player1Time;
      this.player2TimeRemaining = data.player2Time;
      this.updateChessClockDisplay();
    };
    socketService.onChessClockUpdate(this._chessClockHandler);
    // Listen for rematch accepted to show who goes first
    socketService.onRematchAccepted((d) => {
      if (!d || !d.game || d.game.id !== this.currentGame?.id) return;
      // Determine which player is PLAYER_1 in the new game
      const mySocketId = socketService.socketId;
      const player1Socket = d.game.players?.[0]?.socketId;
      const message =
        player1Socket === mySocketId
          ? 'Rematch accepted — You go first'
          : 'Rematch accepted — Opponent goes first';
      this.showTransientMessage(message, 3000);
    });
  }

  private attachEventListeners(): void {
    this.container
      .querySelector('#resign-btn-gameplay')
      ?.addEventListener('click', () => this.handleResign());
    this.container
      .querySelector('#exit-to-lobby-btn-gameplay')
      ?.addEventListener('click', () => this.handleExitToLobby());
    this.container
      .querySelector('#rematch-btn-gameplay')
      ?.addEventListener('click', () => this.handleRematchRequest());
  }

  private renderBoard(): void {
    const container = this.container.querySelector('#game-board-container');
    if (!container || !this.board) return;

    const cellSize = 28;
    const boardSize = this.board.getSize();

    container.innerHTML = `
      <div class="board" id="board" style="grid-template-columns: repeat(${boardSize}, ${cellSize}px);">
        ${Array.from({ length: boardSize * boardSize }, (_, i) => {
          const row = Math.floor(i / boardSize);
          const col = i % boardSize;
          return `<div class="cell" data-row="${row}" data-col="${col}"></div>`;
        }).join('')}
      </div>
    `;

    // Attach cell click listeners
    container.querySelectorAll('.cell').forEach((cell) => {
      cell.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        const row = parseInt(target.dataset.row || '0');
        const col = parseInt(target.dataset.col || '0');
        this.handleCellClick(row, col);
      });
    });

    // If there's a last move recorded, mark it visually
    if (this.lastMove) {
      const selector = `.cell[data-row="${this.lastMove.row}"][data-col="${this.lastMove.col}"]`;
      const lastEl = container.querySelector(selector);
      if (lastEl) lastEl.classList.add('last-move');
    }
  }

  public handleCellClick(row: number, col: number): void {
    if (!this.isMyTurn || !this.engine || !this.currentGame) {
      return;
    }

    if (this.engine.makeMove(row, col)) {
      // record last move locally (this client played it)
      this.setLastMove(row, col);
      this.updateBoardUI();
      socketService.makeMove(this.currentGame.id, row, col);
      this.isMyTurn = false;
      this.updateGameStatus();

      // Check if game is over
      if (this.engine.isGameOver()) {
        const gameState = this.engine.getGameState();
        const winnerId =
          gameState === 'PLAYER_X_WON' || gameState === 'PLAYER_O_WON'
            ? socketService.socketId
            : undefined;
        socketService.endGame(this.currentGame.id, winnerId);
      }
    }
  }

  public applyMove(row: number, col: number): void {
    if (!this.engine) return;

    this.engine.makeMove(row, col);
    // Record the last move (remote or local) and update UI
    this.setLastMove(row, col);
    this.updateBoardUI();
    this.updateGameStatus();
  }

  private updateBoardUI(): void {
    if (!this.engine) return;

    const board = this.engine.getBoard();
    const cells = this.container.querySelectorAll('.cell');

    cells.forEach((cell) => {
      const row = parseInt((cell as HTMLElement).dataset.row || '0');
      const col = parseInt((cell as HTMLElement).dataset.col || '0');
      const display = board.getCellDisplay(row, col);

      if (cell.textContent !== display) {
        cell.textContent = display;
      }

      // Update occupied and mark classes so X/O can be styled separately
      if (display !== '') {
        cell.classList.add('occupied');
        // clear previous mark classes
        cell.classList.remove('mark-x', 'mark-o');
        if (display === '🞅') {
          cell.classList.add('mark-o');
        } else if (display === '🞩') {
          cell.classList.add('mark-x');
        }
      } else {
        cell.classList.remove('occupied', 'mark-x', 'mark-o');
      }
      // Update last-move class
      if (
        this.lastMove &&
        this.lastMove.row === row &&
        this.lastMove.col === col
      ) {
        cell.classList.add('last-move');
      } else {
        cell.classList.remove('last-move');
      }
    });
  }

  private clearLastMove(): void {
    if (!this.lastMove) return;
    const container = this.container.querySelector('#game-board-container');
    if (!container) return;
    const selector = `.cell[data-row="${this.lastMove.row}"][data-col="${this.lastMove.col}"]`;
    const lastEl = container.querySelector(selector);
    if (lastEl) lastEl.classList.remove('last-move');
    this.lastMove = null;
  }

  public clearWinningLine(): void {
    if (!this.winningLinePositions) return;
    const container = this.container.querySelector('#game-board-container');
    if (!container) return;
    for (const pos of this.winningLinePositions) {
      const selector = `.cell[data-row="${pos.row}"][data-col="${pos.col}"]`;
      const el = container.querySelector(selector);
      if (el) el.classList.remove('winning');
    }
    this.winningLinePositions = null;
  }

  public highlightWinningLine(
    positions: Array<{ row: number; col: number }>
  ): void {
    if (!positions || positions.length === 0) return;
    this.clearWinningLine();
    this.winningLinePositions = positions.map((p) => ({
      row: p.row,
      col: p.col,
    }));
    const container = this.container.querySelector('#game-board-container');
    if (!container) return;
    for (const pos of this.winningLinePositions) {
      const selector = `.cell[data-row="${pos.row}"][data-col="${pos.col}"]`;
      const el = container.querySelector(selector);
      if (el) el.classList.add('winning');
    }
  }

  private setLastMove(row: number, col: number): void {
    // Remove previous highlight if different
    if (
      this.lastMove &&
      (this.lastMove.row !== row || this.lastMove.col !== col)
    ) {
      this.clearLastMove();
    }
    this.lastMove = { row, col };
  }

  private updateGameStatus(): void {
    this.updateChessClockDisplay();
  }

  private updateChessClockDisplay(): void {
    const player1TimeEl = this.container.querySelector('#player1-time');
    const player2TimeEl = this.container.querySelector('#player2-time');

    if (!player1TimeEl || !player2TimeEl) return;

    // Format time as MM:SS
    const formatTime = (seconds: number): string => {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins.toString().padStart(2, '0')}:${secs
        .toString()
        .padStart(2, '0')}`;
    };

    player1TimeEl.textContent = formatTime(this.player1TimeRemaining);
    player2TimeEl.textContent = formatTime(this.player2TimeRemaining);

    // Add urgent class if time is low (< 30 seconds)
    if (this.player1TimeRemaining < 30) {
      player1TimeEl.classList.add('urgent');
    } else {
      player1TimeEl.classList.remove('urgent');
    }

    if (this.player2TimeRemaining < 30) {
      player2TimeEl.classList.add('urgent');
    } else {
      player2TimeEl.classList.remove('urgent');
    }

    // Highlight active player's time in green
    if (this.engine) {
      const currentPlayerMark = this.engine.getCurrentPlayer().mark;
      if (currentPlayerMark === 'X') {
        player1TimeEl.classList.add('active');
        player2TimeEl.classList.remove('active');
      } else {
        player1TimeEl.classList.remove('active');
        player2TimeEl.classList.add('active');
      }
    }
  }

  private startChessClock(): void {
    // No-op: server emits tick events; frontend listens via SocketService.onChessClockUpdate
  }

  public stopChessClock(): void {
    // Nothing to clear here; the server drives the clock. If we had any local timer it would be cleared.
  }

  private handleTimeOut(player: 'PLAYER_1' | 'PLAYER_2'): void {
    this.stopChessClock();

    if (!this.currentGame) return;

    const winnerId =
      player === 'PLAYER_1'
        ? this.currentGame.players[1]?.socketId
        : this.currentGame.players[0]?.socketId;

    socketService.endGame(this.currentGame.id, winnerId);
  }

  private handleResign(): void {
    if (!this.currentGame) return;

    const confirmed = confirm(
      'Are you sure you want to resign? This will count as a loss.'
    );
    if (!confirmed) return;

    const opponentId = this.currentGame.players.find(
      (p) => p.socketId !== socketService.socketId
    )?.socketId;
    socketService.endGame(this.currentGame.id, opponentId);
  }

  private handleExitToLobby(): void {
    if (!this.currentGame) {
      this.onExitToLobby();
      return;
    }

    // Only warn if game is in progress (not ended)
    if (this.gameInProgress && this.engine && !this.engine.isGameOver()) {
      const confirmed = confirm(
        'Are you sure you want to exit to lobby? This will count as a loss.'
      );
      if (!confirmed) return;

      const opponentId = this.currentGame.players.find(
        (p) => p.socketId !== socketService.socketId
      )?.socketId;
      socketService.endGame(this.currentGame.id, opponentId);
    }

    // Notify opponent to also exit to lobby
    socketService.exitToLobby(this.currentGame.id);

    this.onExitToLobby();
  }

  private handleRematchRequest(): void {
    if (!this.lastOpponentId) {
      return;
    }

    this.rematchRequested = true;
    socketService.requestRematch(this.lastOpponentId);

    const gameplayStatusEl = this.container.querySelector(
      '#rematch-status-gameplay'
    );
    if (gameplayStatusEl) {
      gameplayStatusEl.textContent = '⏳ Waiting for opponent...';
    }
  }

  public switchGameControlsToRematch(): void {
    const resignBtn = this.container.querySelector('#resign-btn-gameplay');
    const rematchBtn = this.container.querySelector('#rematch-btn-gameplay');

    resignBtn?.classList.add('hidden');
    rematchBtn?.classList.remove('hidden');

    // Mark game as no longer in progress
    this.gameInProgress = false;
    // Optionally record the last match into local history if we can infer result and elo
    try {
      // Attempt to read last game result and elo change from the DOM if available via MultiplayerUI flow
      // MultiplayerUI will call addLocalMatchToHistory directly when it has data; this is just a fallback noop.
    } catch (err) {
      // ignore
    }
  }

  // Add a local match entry shown in the left panel (non-persistent)
  public addLocalMatchToHistory(
    opponentName: string,
    result: 'win' | 'loss' | 'draw',
    eloChange: number
  ): void {
    const entry = { opponentName, result, eloChange, timestamp: Date.now() };
    this.localMatchHistory.unshift(entry);
    // Keep only the last 20 entries
    if (this.localMatchHistory.length > 20) this.localMatchHistory.length = 20;
    this.renderLocalMatchHistory();
  }

  private renderLocalMatchHistory(): void {
    const listEl = this.container.querySelector('#local-match-list');
    if (!listEl) return;
    if (this.localMatchHistory.length === 0) {
      listEl.innerHTML = '<p class="empty">No recent matches</p>';
      return;
    }

    listEl.innerHTML = this.localMatchHistory
      .map((entry) => {
        const date = new Date(entry.timestamp);
        const time = `${date.getHours().toString().padStart(2, '0')}:${date
          .getMinutes()
          .toString()
          .padStart(2, '0')}`;
        const sign = entry.eloChange > 0 ? '+' : '';
        return `
        <div class="local-match-entry" role="listitem" aria-label="Match vs ${
          entry.opponentName
        }">
          <div class="mh-left"><strong>${entry.opponentName}</strong></div>
          <div class="mh-right"><span class="muted">${time}</span> ${entry.result.toUpperCase()} <span class="elo-change">${sign}${
          entry.eloChange
        }</span></div>
        </div>
      `;
      })
      .join('');
  }

  public switchGameControlsToResign(): void {
    const resignBtn = this.container.querySelector('#resign-btn-gameplay');
    const rematchBtn = this.container.querySelector('#rematch-btn-gameplay');

    resignBtn?.classList.remove('hidden');
    rematchBtn?.classList.add('hidden');

    // Mark game as in progress (for rematch)
    this.gameInProgress = true;
  }

  public showRematchRequest(
    requesterName: string,
    requesterId: string,
    onAccept: () => void,
    onDecline: () => void
  ): void {
    const statusEl = this.container.querySelector('#rematch-status-gameplay');
    if (statusEl) {
      statusEl.innerHTML = `
        <p>${requesterName} wants a rematch!</p>
        <button id="accept-rematch-btn" class="primary-btn">✓ Accept</button>
        <button id="decline-rematch-btn" class="secondary-btn">✗ Decline</button>
      `;

      this.container
        .querySelector('#accept-rematch-btn')
        ?.addEventListener('click', onAccept);
      this.container
        .querySelector('#decline-rematch-btn')
        ?.addEventListener('click', onDecline);
    }
  }

  public showRematchStatus(message: string): void {
    const statusEl = this.container.querySelector('#rematch-status-gameplay');
    if (statusEl) {
      statusEl.textContent = message;
    }
  }

  private showTransientMessage(
    message: string,
    durationMs: number = 2500
  ): void {
    // Simple transient banner near rematch status
    const statusEl = this.container.querySelector('#rematch-status-gameplay');
    if (!statusEl) return;

    const prev = statusEl.textContent;
    statusEl.textContent = message;
    statusEl.classList.add('transient');

    setTimeout(() => {
      statusEl.classList.remove('transient');
      statusEl.textContent = prev || '';
    }, durationMs);
  }

  public setIsMyTurn(value: boolean): void {
    this.isMyTurn = value;
  }

  public getEngine(): GameEngine {
    return this.engine;
  }

  public getCurrentGame(): GameRoom {
    return this.currentGame;
  }

  // Return a shallow copy of local match history for preservation across rematches
  public getLocalMatchHistory(): Array<{
    opponentName: string;
    result: 'win' | 'loss' | 'draw';
    eloChange: number;
    timestamp: number;
  }> {
    return [...this.localMatchHistory];
  }

  // Replace the current local match history (used when recreating GameplayScreen for rematch)
  public setLocalMatchHistory(
    history: Array<{
      opponentName: string;
      result: 'win' | 'loss' | 'draw';
      eloChange: number;
      timestamp: number;
    }>
  ): void {
    this.localMatchHistory = Array.isArray(history) ? history.slice(0, 20) : [];
    this.renderLocalMatchHistory();
  }

  public destroy(): void {
    // Remove the specific chess clock listener
    if (this._chessClockHandler) {
      socketService.off('chess-clock-update');
      this._chessClockHandler = null;
    }
    // Also remove rematch listener registered in render
    socketService.off('rematch-accepted');
  }
}
